import { applyFields, validateOrderEvent, type EventRow, type Id, type LwwEntity } from '@odhaczka/core';
import { v7 as uuidv7 } from 'uuid';
import { syncEngine } from '../sync/engine';
import { tick } from './clock';
import { db, lwwTable } from './db';
import type { LwwRowsByEntity } from './types';

type Fields<E extends LwwEntity> = Partial<Omit<LwwRowsByEntity[E], 'id' | 'fieldHlc'>>;

export interface Writer {
  /** Zmienia pola encji (tworzy wiersz, jeśli nie istnieje). */
  set<E extends LwwEntity>(entity: E, id: Id, fields: Fields<E>): Promise<void>;
  /** Dopisuje zdarzenie do dziennika kolejności; hlc i id uzupełnia sam. */
  append(event: Omit<EventRow, 'hlc' | 'id'> & { id?: Id }): Promise<EventRow>;
}

/**
 * Jedna lokalna transakcja: zmiany encji + wpisy do outboxa (+ zdarzenia) razem albo wcale.
 * Po zatwierdzeniu planuje synchronizację.
 */
export async function commit<T>(build: (w: Writer) => Promise<T>): Promise<T> {
  const result = await db.transaction('rw', db.tables, async () => {
    const writer: Writer = {
      async set(entity, id, fields) {
        const hlc = await tick();
        const table = lwwTable(entity);
        const current = await table.get(id);
        const { row, changed } = applyFields(current as never, id, fields as Record<string, unknown>, hlc);
        if (changed.length === 0) return;
        await table.put(row as never);
        await db.outbox.add({
          mutation: { id: uuidv7(), entity, entityId: id, op: 'set', fields: fields as Record<string, unknown>, hlc },
        });
      },
      async append(partial) {
        const hlc = await tick();
        const event = { ...partial, id: partial.id ?? uuidv7(), hlc } as EventRow;
        // Błąd programisty: serwer odrzuciłby zdarzenie tą samą walidacją, a model tego telefonu by się rozjechał.
        const problem = validateOrderEvent(event);
        if (problem) throw new Error(`Niepoprawne zdarzenie ${event.kind}: ${problem}`);
        await db.orderEvents.put(event);
        await db.outbox.add({ mutation: { id: uuidv7(), entity: 'orderEvents', entityId: event.id, op: 'append', event, hlc } });
        return event;
      },
    };
    return build(writer);
  });
  syncEngine.schedule();
  return result;
}

export const newId = (): Id => uuidv7();
